/**
 * TimelineEventManager.js - Manages timeline events for historical characters
 * 
 * This module handles generating timeline-based messages when characters are loaded,
 * checks for events based on current date, and manages notifications for the bio section.
 */

export class TimelineEventManager {
    constructor() {
        this.currentTimelineEvent = null;
        this.hasNewEvent = false;
        this.listeners = [];
        
        // Initialize event listener for model loading
        this.initializeEventListeners();
    }
    
    /**
     * Initialize event listeners for model loading
     */
    initializeEventListeners() {
        document.addEventListener('modelLoadingComplete', (event) => {
            console.log('TimelineEventManager: Model loading complete, checking for timeline events');
            this.checkForTimelineEvent();
        });
        
        // Also listen for character changes
        document.addEventListener('character-changed', (event) => {
            if (event.detail && event.detail.modelId) {
                console.log('TimelineEventManager: Character changed, checking for timeline events');
                this.checkForTimelineEvent(event.detail.modelId);
            }
        });
    }
    
    /**
     * Check for timeline events based on current date
     */
    async checkForTimelineEvent(modelId = null) {
        try {
            // Get current model ID if not provided
            if (!modelId) {
                modelId = this.getCurrentModelId();
            }
            
            if (!modelId) {
                console.log('TimelineEventManager: No model ID available');
                return;
            }
            
            // Get current date
            const currentDate = new Date();
            const month = currentDate.getMonth() + 1; // 0-indexed
            const day = currentDate.getDate();
            
            const monthNames = ["", "January", "February", "March", "April", "May", "June",
                              "July", "August", "September", "October", "November", "December"];
            const monthName = monthNames[month] || month;
            
            console.log(`🔍 TimelineEventManager: Checking dynamic OpenAI timeline events for model ${modelId} in ${monthName}`);
            
            // Call backend to check for timeline events using OpenAI (now month-based)
            const response = await fetch('/api/timeline-event', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({
                    character_id: modelId,
                    month: month,
                    day: day
                })
            });
            
            if (response.ok) {
                const eventData = await response.json();
                
                if (eventData.has_event) {
                    console.log(`✅ TimelineEventManager: Dynamic timeline event found via OpenAI for ${monthName}:`, eventData.message);
                    this.currentTimelineEvent = eventData;
                    this.hasNewEvent = true;
                    
                    // Notify listeners about the new event
                    this.notifyListeners(eventData);
                    
                    // Add notification to bio button
                    this.addBioNotification();
                    
                    // Add event to bio section
                    this.addEventToBioSection(eventData);
                } else {
                    console.log(`⚪ TimelineEventManager: No timeline event for ${monthName} (OpenAI found no significant events)`);
                    this.currentTimelineEvent = null;
                    this.hasNewEvent = false;
                    this.removeBioNotification();
                }
            } else {
                console.error('❌ TimelineEventManager: Failed to fetch timeline event from OpenAI API');
            }
        } catch (error) {
            console.error('TimelineEventManager: Error checking timeline event:', error);
        }
    }
    
    /**
     * Get current model ID from various sources
     */
    getCurrentModelId() {
        // Try to get from ModelManager if available
        if (window.modelManager) {
            return window.modelManager.getCurrentModelId();
        }
        
        // Fallback to localStorage
        const storedModelId = localStorage.getItem('selectedModelId');
        if (storedModelId) {
            return storedModelId;
        }
        
        // Last resort - check URL parameters
        const urlParams = new URLSearchParams(window.location.search);
        return urlParams.get('character');
    }
    
    /**
     * Add notification indicator to bio section button
     */
    addBioNotification() {
        const bioButton = document.querySelector('#bio-button');
        if (!bioButton) return;
        
        // Remove existing notification if any
        this.removeBioNotification();
        
        // Create notification indicator (red dot)
        const notification = document.createElement('div');
        notification.className = 'timeline-notification';
        notification.innerHTML = '●'; // Red dot character
        
        // Add to bio button
        bioButton.style.position = 'relative';
        bioButton.appendChild(notification);
        
        console.log('TimelineEventManager: Added notification to bio button');
    }
    
    /**
     * Remove notification indicator from bio section button
     */
    removeBioNotification() {
        const existingNotification = document.querySelector('.timeline-notification');
        if (existingNotification) {
            existingNotification.remove();
        }
    }
    
    /**
     * Add timeline event to bio section
     */
    addEventToBioSection(eventData) {
        // Find or create timeline events section in bio panel
        let timelineSection = document.querySelector('.bio-timeline-section');
        
        if (!timelineSection) {
            // Create new timeline section
            timelineSection = this.createTimelineSection();
        }
        
        // Add the event message
        const eventElement = document.createElement('div');
        eventElement.className = 'timeline-event-message';
        eventElement.innerHTML = `
            <div class="timeline-event-content">
                <i class="fas fa-calendar-day timeline-event-icon"></i>
                <div class="timeline-event-text">${eventData.message}</div>
            </div>
        `;
        
        // Clear previous events and add new one
        const existingEvents = timelineSection.querySelectorAll('.timeline-event-message');
        existingEvents.forEach(event => event.remove());
        
        const sectionContent = timelineSection.querySelector('.bio-section-content');
        sectionContent.appendChild(eventElement);
        
        // Make sure section is visible (not collapsed)
        timelineSection.classList.remove('collapsed');
        
        console.log('TimelineEventManager: Added timeline event to bio section');
    }
    
    /**
     * Create timeline section in bio panel
     */
    createTimelineSection() {
        const bioPanel = document.querySelector('.bio-panel-content');
        if (!bioPanel) return null;
        
        // Create timeline section
        const timelineSection = document.createElement('div');
        timelineSection.className = 'bio-section bio-timeline-section';
        timelineSection.innerHTML = `
            <div class="bio-section-header" style="background: transparent; border: none;">
                <h3 class="bio-section-title">On This Month</h3>
            </div>
            <div class="bio-section-content"></div>
        `;
        
        // Insert at the beginning of bio panel (after core bio)
        const coreBioSection = bioPanel.querySelector('.bio-section');
        if (coreBioSection && coreBioSection.nextSibling) {
            bioPanel.insertBefore(timelineSection, coreBioSection.nextSibling);
        } else {
            bioPanel.appendChild(timelineSection);
        }
        
        return timelineSection;
    }
    
    /**
     * Add listener for timeline events
     */
    addListener(listener) {
        this.listeners.push(listener);
    }
    
    /**
     * Remove listener for timeline events
     */
    removeListener(listener) {
        const index = this.listeners.indexOf(listener);
        if (index > -1) {
            this.listeners.splice(index, 1);
        }
    }
    
    /**
     * Notify all listeners about timeline events
     */
    notifyListeners(eventData) {
        this.listeners.forEach(listener => {
            try {
                listener(eventData);
            } catch (error) {
                console.error('TimelineEventManager: Error notifying listener:', error);
            }
        });
    }
    
    /**
     * Get current timeline event
     */
    getCurrentEvent() {
        return this.currentTimelineEvent;
    }
    
    /**
     * Check if there's a new event
     */
    hasNewTimelineEvent() {
        return this.hasNewEvent;
    }
    
    /**
     * Mark event as read
     */
    markEventAsRead() {
        this.hasNewEvent = false;
        this.removeBioNotification();
    }
}

// Create global instance
window.timelineEventManager = new TimelineEventManager();